import { supabase } from '../config/supabaseConfig.ts';
import bcrypt from 'bcryptjs';

export const adminUserService = {
  // Merge user_effective_limits (plan/uso/bloqueo) + clientes_usuario (perfil)
  getAll: async () => {
    const [{ data: limits, error: limitsError }, { data: profiles, error: profilesError }] =
      await Promise.all([
        supabase.from('user_effective_limits').select('*'),
        supabase.from('clientes_usuario').select('id, full_name, email, whatsapp_number, auth_user_id, created_at, status'),
      ]);

    if (limitsError) throw limitsError;
    if (profilesError) throw profilesError;

    const profileMap = {};
    (profiles || []).forEach(p => {
      if (p.auth_user_id) profileMap[p.auth_user_id] = p;
    });

    return (limits || []).map(u => {
      const p = profileMap[u.user_id] || {};
      return {
        user_id: u.user_id,
        email: u.email,
        plan_name: u.plan_name,
        is_blocked: u.is_blocked,
        block_reason: u.block_reason,
        text_limit: u.text_limit,
        voice_limit: u.voice_limit,
        text_used: u.text_used,
        voice_used: u.voice_used,
        text_remaining: u.text_remaining,
        voice_remaining: u.voice_remaining,
        days_remaining_in_week: u.days_remaining_in_week,
        // desde clientes_usuario
        profile_id: p.id || null,
        full_name: p.full_name || '',
        whatsapp_number: p.whatsapp_number || '',
        client_status: p.status || null,
        registered_at: p.created_at || null,
      };
    });
  },

  getUserSettings: async (userId) => {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*, plan:plans(id, name, text_limit_monthly, voice_limit_monthly)')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  // Datos para gráfica de uso diario del mes actual
  getUsageChart: async (userId) => {
    const { data, error } = await supabase
      .from('usage_daily_chart')
      .select('date_label, text_count, voice_count')
      .eq('user_id', userId);
    if (error) throw error;
    return data || [];
  },

  // Usuarios de clientes_usuario con auth_user_id que aún NO tienen user_settings
  getAvailableClients: async () => {
    const [{ data: clients, error: clientsError }, { data: settings, error: settingsError }] =
      await Promise.all([
        supabase
          .from('clientes_usuario')
          .select('id, full_name, email, auth_user_id, whatsapp_number')
          .not('auth_user_id', 'is', null)
          .eq('status', 'active')
          .order('full_name'),
        supabase.from('user_settings').select('user_id'),
      ]);

    if (clientsError) throw clientsError;
    if (settingsError) throw settingsError;

    const registered = new Set((settings || []).map(s => s.user_id));
    return (clients || []).filter(c => !registered.has(c.auth_user_id));
  },

  // Registra un cliente existente en el panel: crea user_settings + actualiza perfil + log
  registerClient: async ({ authUserId, planId, adminId, clientData, password, sessionName }) => {
    const hashedPassword = await bcrypt.hash(password, 10);

    const { error } = await supabase
      .from('user_settings')
      .insert({ user_id: authUserId, plan_id: planId, is_blocked: false });
    if (error) throw new Error(`Error al crear configuración: ${error.message}`);

    const { error: profileError } = await supabase
      .from('clientes_usuario')
      .update({ password: hashedPassword, session_name: sessionName })
      .eq('auth_user_id', authUserId);
    if (profileError) throw new Error(`Error al guardar perfil: ${profileError.message}`);

    await supabase.from('admin_log').insert({
      admin_id: adminId,
      target_user_id: authUserId,
      action: 'create_user',
      details: { email: clientData.email, full_name: clientData.full_name, plan_id: planId },
    });
  },

  updateProfile: async (authUserId, updates) => {
    const { error } = await supabase
      .from('clientes_usuario')
      .update(updates)
      .eq('auth_user_id', authUserId);
    if (error) throw error;
  },

  changePlan: async ({ userId, planId, adminId, beforePlanId }) => {
    await _getOrCreateSettings(userId, planId);
    const { error } = await supabase
      .from('user_settings')
      .update({ plan_id: planId, updated_at: new Date().toISOString() })
      .eq('user_id', userId);
    if (error) throw error;

    await supabase.from('admin_log').insert({
      admin_id: adminId,
      target_user_id: userId,
      action: 'change_plan',
      details: { before: { plan_id: beforePlanId }, after: { plan_id: planId } },
    });
  },

  setCustomLimits: async ({ userId, customTextLimit, customVoiceLimit, adminId, before }) => {
    await _getOrCreateSettings(userId, null);

    const updates = { updated_at: new Date().toISOString() };
    if (customTextLimit !== undefined) updates.custom_text_limit_weekly = customTextLimit;
    if (customVoiceLimit !== undefined) updates.custom_voice_limit_weekly = customVoiceLimit;

    const { error } = await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', userId);
    if (error) throw error;

    await supabase.from('admin_log').insert({
      admin_id: adminId,
      target_user_id: userId,
      action: customTextLimit !== undefined ? 'set_text_limit' : 'set_voice_limit',
      details: { before, after: updates },
    });
  },

  setBlockStatus: async ({ userId, isBlocked, blockReason, adminId, before }) => {
    await _getOrCreateSettings(userId, null);

    const updates = {
      is_blocked: isBlocked,
      block_reason: isBlocked ? (blockReason || null) : null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', userId);
    if (error) throw error;

    await supabase.from('admin_log').insert({
      admin_id: adminId,
      target_user_id: userId,
      action: isBlocked ? 'block' : 'unblock',
      details: { before, after: updates },
    });
  },

  resetUsage: async ({ userId, adminId }) => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const daysFromMonday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysFromMonday);
    const mondayStr = monday.toISOString().split('T')[0];

    const { error } = await supabase
      .from('usage_daily')
      .update({ text_count: 0, voice_count: 0, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .gte('date', mondayStr)
      .lte('date', todayStr);
    if (error) throw error;

    await supabase.from('admin_log').insert({
      admin_id: adminId,
      target_user_id: userId,
      action: 'reset_usage',
      details: { week_start: mondayStr, week_end: todayStr },
    });
  },

  getAdminLog: async (targetUserId) => {
    const { data, error } = await supabase
      .from('admin_log')
      .select('*')
      .eq('target_user_id', targetUserId)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const entries = data || [];
    const adminIds = [...new Set(entries.map(e => e.admin_id).filter(Boolean))];

    let adminMap = {};
    if (adminIds.length > 0) {
      const { data: admins } = await supabase
        .from('clientes_usuario')
        .select('auth_user_id, full_name')
        .in('auth_user_id', adminIds);
      (admins || []).forEach(a => { adminMap[a.auth_user_id] = a.full_name || 'Admin'; });
    }

    return entries.map(e => ({
      ...e,
      admin_nombre: adminMap[e.admin_id] || 'Admin',
    }));
  },
};

async function _getOrCreateSettings(userId, planId) {
  const { data } = await supabase
    .from('user_settings')
    .select('user_id, plan_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (!data) {
    const { error } = await supabase.from('user_settings').insert({
      user_id: userId,
      plan_id: planId,
      is_blocked: false,
    });
    if (error) throw error;
  }
  return data;
}
